import { useErrorHandler, uuid } from "@@common";
import AddIcon from "@mui/icons-material/Add";
import AssistantIcon from "@mui/icons-material/Assistant";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { Avatar, Card, CardContent, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { formatTimestamp } from "../../../common/formatTimestamp";
import { ChatModel } from "../../Models/ChatModel";
import IComponentProps from "../IComponentProps";

type Message = {
  id: string;
  text: string;
  userId?: string;
  timestamp: Date;
};

const ChatComponent: React.FC<IComponentProps<ChatModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesPending, setMessagesPending] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  const setInitialMessages = () =>
    setMessages([
      {
        id: uuid(),
        text: component.InitialMessages ?? "Hello! How can I assist you today?",
        timestamp: new Date(),
      },
    ]);

  const handleRefresh = async () => {
    if (!component.GetMessages || !component.MessageTransform) {
      console.warn("GetMessages or MessageTransform expression is not defined.");
      return;
    }

    setMessagesPending(true);
    try {
      const result = await context.Evaluator.evaluate(component.GetMessages, context);
      if (result === undefined || result === null) {
        return;
      }

      if (!Array.isArray(result)) {
        console.error("GetMessages expression did not return an array:", result);
        throw new Error("GetMessages expression did not return an array: " + result);
      }

      // Create a child context for item evaluation, it will be re-used for each item
      const itemContext = context.CreateChild();

      const items = result as any[];
      const messages: Message[] = [];
      for (const item of items) {
        // Set the $source variable to the current item
        itemContext.VariableScope.set("$source", item);
        const message: Message = await itemContext.Evaluator.evaluate(component.MessageTransform!, itemContext);
        messages.push(message);
      }
      setMessages(messages);
    } catch (error) {
      errorHandler(error);
    } finally {
      setMessagesPending(false);
    }
  };

  useEffect(() => {
    async function init() {
      setInitialMessages();
      if (component.OnOpen) {
        // Create a child context for the event
        await context.Evaluator.evaluate(component.OnOpen, context);
      }
      await handleRefresh();
    }
    init();
  }, [component.OnOpen, component.GetMessages, component.MessageTransform, context]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !component.PostMessage) return;

    setIsSending(true);
    try {
      // Create a child context for the event
      const childContext = context.CreateChild();
      childContext.VariableScope.set("$event", {
        text: inputValue.trim(),
      });

      // Trigger the PostMessage expression evaluation
      await childContext.Evaluator.evaluate(component.PostMessage, childContext);

      const newMessage: Message = {
        id: uuid(),
        text: inputValue,
        userId: "user",
        timestamp: new Date(),
      };

      setMessages((messages) => [...messages, newMessage]);
      setInputValue("");
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSending(false);
    }
    await handleRefresh();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = async () => {
    if (!component.OnNewChat) return;

    try {
      setMessagesPending(true);

      setInitialMessages();
      setInputValue("");
      await context.Evaluator.evaluate(component.OnNewChat, context);
    } catch (error) {
      errorHandler(error);
    } finally {
      setMessagesPending(false);
    }
    await handleRefresh();
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", minHeight: "600px", maxHeight: "50vh" }}>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" spacing={1} mb={2}>
          {component.OnNewChat && (
            <IconButton onClick={handleNewChat} disabled={messagesPending} title="New chat" aria-label="new chat">
              <AddIcon />
            </IconButton>
          )}
          <IconButton onClick={handleRefresh} disabled={messagesPending} title="Refresh messages" aria-label="refresh messages" loading={messagesPending}>
            <RefreshIcon />
          </IconButton>
        </Stack>
        <Stack direction="column" flex={1} spacing={2} overflow="auto">
          {messages.map((message) => (
            <Stack
              key={message.id}
              direction="row"
              sx={{
                alignSelf: message.userId ? "flex-end" : "flex-start",
                maxWidth: "80%",
                flexDirection: message.userId ? "row-reverse" : "row",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: message.userId ? "primary.main" : "secondary.main",
                  width: 32,
                  height: 32,
                  marginLeft: 1,
                  marginRight: 1,
                }}
              >
                {message.userId ? <PersonIcon fontSize="small" /> : <AssistantIcon fontSize="small" />}
              </Avatar>
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  p: 1,
                  backgroundColor: message.userId ? "primary.dark" : "background.paper",
                  color: "text.primary",
                  border: (theme) => (message.userId ? `1px solid ${theme.palette.primary.main}` : "none"),
                  minWidth: "200px",
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {message.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: "text.secondary",
                  }}
                >
                  {formatTimestamp(message.timestamp)}
                </Typography>
              </Paper>
            </Stack>
          ))}
          <div ref={messagesEndRef} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" paddingTop={2}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            variant="outlined"
            disabled={isSending}
          />
          <IconButton color="primary" onClick={handleSendMessage} disabled={!inputValue.trim() || isSending} loading={isSending} aria-label="send message">
            <SendIcon />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ChatComponent;
